﻿import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { useAuthenticationStore } from '@/features/auth/stores/authenticationStore.js';
import { useClient } from '@/plugins/api.js';
import { useSessionStorage } from '@vueuse/core';
import { useLanguageStore } from '@/stores/languageStore.js';

export const useUserProfileStore = defineStore('user-profile', () => {
    const auth = useAuthenticationStore();
    const languageStore = useLanguageStore();
    const isUpdating = ref(false);
    const isUploadingPortrait = ref(false);
    const isLoadingCalendarFeed = ref(false);
    const isUpdatingCalendarFeed = ref(false);
    const calendarExportFeed = ref(null);
    const error = ref(null);

    const authWatcher = watch(
        () => auth.isAuthenticated,
        async newValue => {
            if (newValue) {
                await fetchCurrentUserProfile();
                await fetchCalendarExportFeed();
            } else if (!auth.isRefreshing) {
                value.value = undefined;
                calendarExportFeed.value = null;
            }
        }
    );

    const value = useSessionStorage('user-profile', {}, { writeDefaults: false });

    const fullname = computed(() => {
        if (value.value) {
            const { firstname, lastname } = value.value;

            if (firstname && lastname) {
                return `${lastname}, ${firstname}`;
            } else if (firstname) {
                return firstname;
            } else if (lastname) {
                return lastname;
            }
        }
        return 'n/a';
    });

    const alias = computed(() => {
        if (value.value) {
            return (
                value.value.alias || `${value.value.firstname || ''} ${value.value.lastname || ''}`.trim() || 'Anonyme'
            );
        }
        return 'Anonyme';
    });

    const portraitUrl = computed(() => {
        return value.value && value.value.portraitUrl ? value.value.portraitUrl : null;
    });

    const roles = computed(() => value.value?.userRoles ?? []);
    const persona = computed(() => value.value?.persona ?? null);
    const authorizedWorkspaceIds = computed(() => value.value?.authorizedWorkspaceIds ?? []);
    const defaultOrganizationId = computed(() => value.value?.defaultOrganizationId ?? null);

    async function fetchCurrentUserProfile() {
        try {
            const client = useClient();
            const userResponse = await client.get('/api/users/profile');
            value.value = userResponse.data;
            languageStore.setLocale(userResponse.data?.preferredLanguage ?? 'en');
        } catch (fetchError) {
            console.error(fetchError);
        }
    }

    async function changeFullname(firstname, lastname) {
        isUpdating.value = true;
        error.value = null;

        try {
            const client = useClient();
            await client.post(`/api/users/fullname`, {
                firstname: firstname,
                lastname: lastname,
            });
            value.value.firstname = firstname;
            value.value.lastname = lastname;
        } catch (updateError) {
            console.error(updateError);
            error.value = 'Failed to update profile.';
            throw updateError;
        } finally {
            isUpdating.value = false;
        }
    }

    async function changeAlias(alias) {
        isUpdating.value = true;
        error.value = null;

        try {
            const client = useClient();
            await client.post(`/api/users/alias`, {
                alias: alias,
            });
            value.value.alias = alias;
        } catch (updateError) {
            console.error(updateError);
            error.value = 'Failed to update profile.';
            throw updateError;
        } finally {
            isUpdating.value = false;
        }
    }

    async function changeBirthday(birthdate) {
        try {
            const client = useClient();
            await client.post(`/api/users/birthdate`, {
                birthdate: birthdate,
            });
            value.value.birthDate = birthdate;
        } catch (error) {
            console.error(error);
        }
    }

    async function changePhone(phoneNumber) {
        try {
            const client = useClient();
            await client.post(`/api/users/phone`, {
                phoneNumber: phoneNumber,
            });
            value.value.phoneNumber = phoneNumber;
        } catch (error) {
            console.error(error);
        }
    }

    async function changeEmail(email) {
        isUpdating.value = true;
        error.value = null;

        try {
            const client = useClient();
            const response = await client.post(`/api/users/email`, {
                email: email,
            });
            return response.data;
        } catch (updateError) {
            console.error(updateError);
            error.value = 'Failed to update profile.';
            throw updateError;
        } finally {
            isUpdating.value = false;
        }
    }

    async function changePreferredLanguage(preferredLanguage) {
        isUpdating.value = true;
        error.value = null;

        try {
            const client = useClient();
            await client.post(`/api/users/preferred-language`, {
                preferredLanguage: preferredLanguage,
            });
            value.value.preferredLanguage = preferredLanguage;
            languageStore.setLocale(preferredLanguage);
        } catch (updateError) {
            console.error(updateError);
            error.value = 'Failed to update profile.';
            throw updateError;
        } finally {
            isUpdating.value = false;
        }
    }

    async function changeDefaultOrganization(organizationId) {
        isUpdating.value = true;
        error.value = null;

        try {
            const client = useClient();
            const response = await client.post(`/api/users/default-organization`, {
                organizationId: organizationId ?? null,
            });
            value.value.defaultOrganizationId = response.data?.defaultOrganizationId ?? null;
            return value.value.defaultOrganizationId;
        } catch (updateError) {
            console.error(updateError);
            error.value = 'Failed to update profile.';
            throw updateError;
        } finally {
            isUpdating.value = false;
        }
    }

    async function changeAddress(address) {
        try {
            const client = useClient();
            await client.post(`/api/users/address`, {
                address: address,
            });
            value.value.address = address;
        } catch (error) {
            console.error(error);
        }
    }

    async function changePortrait(selectedFile) {
        isUploadingPortrait.value = true;
        error.value = null;

        try {
            const client = useClient();
            const formData = new FormData();
            formData.append('file', selectedFile, selectedFile.name || 'portrait.png');

            const response = await client.post(`/api/users/portrait`, formData);

            value.value.portraitUrl = `${response.data.blobUrl}?${Date.now()}`; // the Date.now() is for cache-busting
        } catch (uploadError) {
            console.error(uploadError);
            error.value = 'Failed to update portrait.';
            throw uploadError;
        } finally {
            isUploadingPortrait.value = false;
        }
    }

    async function fetchCalendarExportFeed() {
        if (!auth.isAuthenticated) {
            calendarExportFeed.value = null;
            return;
        }

        isLoadingCalendarFeed.value = true;

        try {
            const client = useClient();
            const response = await client.get('/api/calendar-integrations/export-feed');
            calendarExportFeed.value = response.data;
        } catch (fetchError) {
            console.error(fetchError);
            error.value = 'Failed to load calendar export feed.';
        } finally {
            isLoadingCalendarFeed.value = false;
        }
    }

    async function enableCalendarExportFeed() {
        return updateCalendarExportFeed('/api/calendar-integrations/export-feed/enable', 'post');
    }

    async function regenerateCalendarExportFeed() {
        return updateCalendarExportFeed('/api/calendar-integrations/export-feed/regenerate', 'post');
    }

    async function revokeCalendarExportFeed() {
        return updateCalendarExportFeed('/api/calendar-integrations/export-feed', 'delete');
    }

    async function updateCalendarExportFeed(url, method) {
        isUpdatingCalendarFeed.value = true;
        error.value = null;

        try {
            const client = useClient();
            const response = await client[method](url);
            calendarExportFeed.value = response.data;
            return response.data;
        } catch (updateError) {
            console.error(updateError);
            error.value = 'Failed to update calendar export feed.';
            throw updateError;
        } finally {
            isUpdatingCalendarFeed.value = false;
        }
    }

    return {
        user: value,
        alias,
        fullname,
        portraitUrl,
        isUpdating,
        isUploadingPortrait,
        isLoadingCalendarFeed,
        isUpdatingCalendarFeed,
        calendarExportFeed,
        error,
        roles,
        persona,
        authorizedWorkspaceIds,
        defaultOrganizationId,
        fetchCurrentUserProfile,
        changeFullname,
        changeAlias,
        changeBirthday,
        changePhone,
        changeEmail,
        changePreferredLanguage,
        changeDefaultOrganization,
        changeAddress,
        changePortrait,
        fetchCalendarExportFeed,
        enableCalendarExportFeed,
        regenerateCalendarExportFeed,
        revokeCalendarExportFeed,
    };
});
